import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import axios from "../../../utils/axios";
import { formatDoSensorChartTime, formatDoSensorReportDateTime } from "../../../utils/doSensorDateTime";

const toLocalInput = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export default function DoSensorHistory({ deviceId, endpoint = "view/do_sensor/history", view = "chart", showControls = true }) {
  const [start, setStart] = useState(() => toLocalInput(new Date(Date.now() - 24 * 60 * 60 * 1000)));
  const [end, setEnd] = useState(() => toLocalInput(new Date()));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const requestId = useRef(0);

  const load = useCallback(async (signal) => {
    const currentRequest = ++requestId.current;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime()) ||
      startDate >= endDate || endDate - startDate > 7 * 24 * 60 * 60 * 1000) {
      setError("Choose a valid date range of up to 7 days.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(endpoint, {
        deviceId,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      }, { signal });
      if (currentRequest === requestId.current) {
        setRows(response.data?.data || []);
        setPage(0);
      }
    } catch (requestError) {
      if (currentRequest === requestId.current && requestError.code !== "ERR_CANCELED") {
        setRows([]);
        setError(requestError.response?.data?.message || "Could not load DO sensor history.");
      }
    } finally {
      if (currentRequest === requestId.current && !signal?.aborted) setLoading(false);
    }
  }, [deviceId, endpoint, start, end]);

  useEffect(() => {
    const controller = new AbortController();
    if (deviceId) load(controller.signal);
    return () => controller.abort();
    // Load the initial 24-hour range and when the device changes; Apply handles later edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const chartRows = useMemo(() => {
    const stride = Math.max(1, Math.ceil(rows.length / 600));
    return rows.filter((_, index) => index % stride === 0 || index === rows.length - 1).map((row) => ({
      time: new Date(row.date_time).getTime(),
      dissolved_oxygen: row.dissolved_oxygen == null ? null : Number(row.dissolved_oxygen),
      temp: row.temp == null ? null : Number(row.temp),
    }));
  }, [rows]);

  const exportCsv = () => {
    const header = ["Date and time", "Dissolved oxygen (mg/L)", "Temperature (°C)"];
    const lines = rows.map((row) => [
      formatDoSensorReportDateTime(row.date_time), row.dissolved_oxygen, row.temp,
    ].map(csvCell).join(","));
    const csv = "\uFEFF" + [header.map(csvCell).join(","), ...lines].join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `do_sensor_${deviceId}_${start.slice(0, 10)}_${end.slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div id="do-sensor-history">
      {showControls && <div className="do-history-controls">
        <label><span className="input-label">Start date</span><input type="datetime-local" value={start} onChange={(event) => { requestId.current++; setStart(event.target.value); setRows([]); setLoading(false); }} /></label>
        <label><span className="input-label">End date</span><input type="datetime-local" value={end} onChange={(event) => { requestId.current++; setEnd(event.target.value); setRows([]); setLoading(false); }} /></label>
        <button className="white-btn" type="button" onClick={() => load()} disabled={loading}>Submit</button>
        <button className="white-btn" type="button" onClick={exportCsv} disabled={loading || rows.length === 0}>Export CSV ({rows.length})</button>
      </div>}
      {error && <p role="alert" className="text-danger">{error}</p>}
      {loading ? <p>Loading chart data...</p> : rows.length === 0 ? <p>No data available for this time period</p> : view === "table" ? (
        <div style={{ overflowX: "auto" }}>
          <table className="table table-striped">
            <thead><tr><th>Date and time</th><th>Dissolved oxygen (mg/L)</th><th>Temperature (°C)</th></tr></thead>
            <tbody>{rows.slice(page * 100, (page + 1) * 100).map((row, index) => (
              <tr key={`${row.date_time}-${index}`}>
                <td>{formatDoSensorReportDateTime(row.date_time)}</td>
                <td>{row.dissolved_oxygen == null ? "--" : Number(row.dissolved_oxygen).toFixed(2)}</td>
                <td>{row.temp == null ? "--" : Number(row.temp).toFixed(2)}</td>
              </tr>
            ))}</tbody>
          </table>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
            <span>Page {page + 1} of {Math.ceil(rows.length / 100)}</span>
            <button type="button" disabled={(page + 1) * 100 >= rows.length} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>
      ) : (
        <div style={{ width: "100%", height: 360 }}>
          <ResponsiveContainer>
            <LineChart data={chartRows} margin={{ top: 10, right: 30, left: 10, bottom: 15 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" type="number" domain={["dataMin", "dataMax"]} scale="time"
                tickFormatter={formatDoSensorChartTime} minTickGap={40} />
              <YAxis yAxisId="do" label={{ value: "DO (mg/L)", angle: -90, position: "insideLeft" }} />
              <YAxis yAxisId="temp" orientation="right" label={{ value: "°C", angle: 90, position: "insideRight" }} />
              <Tooltip labelFormatter={formatDoSensorReportDateTime} />
              <Legend />
              <Line yAxisId="do" type="monotone" dataKey="dissolved_oxygen" name="Dissolved oxygen (mg/L)" stroke="#0288d1" dot={false} connectNulls={false} />
              <Line yAxisId="temp" type="monotone" dataKey="temp" name="Temperature (°C)" stroke="#ef6c00" dot={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
